/**
 * Get the Moment.js format string based on the current locale.
 * This function generates a date-time format string for use with Moment.js
 * by evaluating the current locale's date and time formatting conventions.
 *
 * @param {boolean} useDate - Whether to include date components (year, month, day) in the format string.
 * @param {boolean} useTime - Whether to include time components (hour, minute) in the format string.
 * @param {boolean} useTwelveHourPeriod - Whether to use a 12-hour clock format with AM/PM notation.
 * @returns {string} The formatted Moment.js string that corresponds to the locale-specific date and time format.
 *
 * @example
 * getLocaleFormat(true, true, true);
 * // Returns a format like 'MM/DD/YYYY hh:mm A' for en-US locale
 */
export default function getLocaleFormat(useDate, useTime, useTwelveHourPeriod) {
  let locale = navigator.language;

  // Restricts locale usage to English variants only;
  // other locales are replaced with "en-US".
  if (!/^en/g.test(locale)) {
    locale = 'en-US';
  }

  const year = 2024;
  const month = 4;
  const date = 5;
  const hours = 6;
  const minutes = 7;

  const dateStr = new Date(year, month - 1, date, hours, minutes).toLocaleString(locale, {
    year: useDate ? 'numeric' : undefined,
    month: useDate ? 'numeric' : undefined,
    day: useDate ? 'numeric' : undefined,
    hour: useTime ? 'numeric' : undefined,
    minute: useTime ? 'numeric' : undefined,
    hour12: useTime && useTwelveHourPeriod,
  });

  let format = dateStr;
  format = format.replace(year, 'YYYY');
  format = format.replace(month, 'M');
  format = format.replace(date, 'D');
  format = format.replace(hours, 'h');
  format = format.replace(minutes, 'm');
  format = format.replace(/0(\D)/g, '$1$1');
  format = format.replace(/am|pm/gi, 'A');

  return format;
}
